import { datadogRum } from '@datadog/browser-rum';
import { get as _get } from 'lodash';
import { getClaimDetails } from '../api/getClaimDetailsApi';
import { isPropertyFNOLEnabled } from '../app/featureToggle/featureToggle';
import { updateClaimDetailState } from '../app/store/ClaimDetails/ClaimDetailSlice';
import { updateAuthToken } from '../app/store/ContextRouter/ContextRouterActions';
import {
  getAutoEligibleServices,
  getPropertyEligibleServices,
  updateEligibleServiceState,
} from '../app/store/EligibilityService/EligibilityServiceSlice';
import { store } from '../app/store/store';
import { ContextRouterInfo } from '../model/context-router-model';
import {
  AutoEligibilityServiceCallArguments,
  AutoEligibilityServicesResponse,
  ClaimProgressEnum,
  EligibleServicesConstants,
  GenericProductType,
  PropertyEligibilityServiceCallArguments,
  PropertyEligibilityServicesResponse,
  ServiceTypeCode,
} from '../types/type';
import { getClaimType, getUnformattedClaimNumber, mapAutoClaimDetail } from '../utils/Utilities';

const itemsToHide: string[] = [];

/**
 * Tracks the most recently started setupPostFnol call. Older, in-flight calls (e.g. a
 * draft-resume lookup still retrying when a real submission happens) check this before
 * writing to Redux and drop their response if a newer call has since started, so a stale
 * response can never clobber more recent claim/eligibility data.
 */
let latestSetupPostFnolSequence = 0;

export async function setupPostFnol(
  customerInfo: ContextRouterInfo,
  claimNumber: string,
  urlKey: string,
  sessionId: string,
  authKey: string,
  getEligibleServices: boolean,
  opCoInfo: string,
  origin: string
) {
  const sequence = ++latestSetupPostFnolSequence;
  const claimDetailResponse = await initiateClaimDetailsCall(claimNumber, urlKey, sessionId, authKey, sequence, origin);
  if (claimDetailResponse && !claimDetailResponse.error) {
    const claimType = getClaimType(_get(claimDetailResponse, 'response[0].claimInfo.policyType', ''));
    if (
      claimType === GenericProductType.Auto ||
      (claimType === GenericProductType.Home && isPropertyFNOLEnabled()) ||
      claimType === GenericProductType.CommercialAuto ||
      claimType === GenericProductType.CommercialProperty ||
      claimType === GenericProductType.WorkersComp
    ) {
      // Get current status of claim
      const currentProgressState = _get(
        claimDetailResponse,
        'response[0].claimInfo.claimProgress',
        ClaimProgressEnum.SUBMISSION
      );

      // Eligible services API call is not required for CLOSED Claim
      if (getEligibleServices && currentProgressState.toUpperCase() !== ClaimProgressEnum.CLOSED) {
        await getEligibilityDetails(
          claimDetailResponse,
          customerInfo,
          opCoInfo,
          claimNumber,
          urlKey,
          sessionId,
          authKey,
          sequence,
          origin
        );
      }
      // Regardless of the result of above call, navigate to the next screen
      return claimType;
    }
    return '';
  } else {
    return claimDetailResponse;
  }
}

function isStaleSequence(sequence: number): boolean {
  return sequence !== latestSetupPostFnolSequence;
}

function logStaleResponseDropped(context: { action: string; claimNumber: string; sequence: number; origin: string }) {
  const { action, claimNumber, sequence, origin } = context;
  console.warn(`${action} dropped as stale`, {
    claimNumber,
    sequence,
    latestSequence: latestSetupPostFnolSequence,
    origin,
  });
  datadogRum.addAction('stale-claim-detail-response-dropped', {
    action,
    claimNumber,
    sequence,
    latestSequence: latestSetupPostFnolSequence,
    origin,
  });
}

async function getEligibilityDetails(
  claimDetailResponse: any,
  customerInfo: ContextRouterInfo,
  opCoInfo: string,
  claimNumber: string,
  urlKey: string,
  sessionId: string,
  authToken: string,
  sequence: number,
  origin: string
) {
  const claimType = getClaimType(_get(claimDetailResponse, 'response[0].claimInfo.policyType', ''));

  if (claimType === GenericProductType.Auto) {
    const autoDetails = mapAutoClaimDetail(_get(claimDetailResponse, 'response[1]', []));
    let vin = '';
    if (autoDetails[0]) {
      vin = autoDetails[0].vin ? autoDetails[0].vin : '';
    }
    const apiParamsData: AutoEligibilityServiceCallArguments = {
      claimNumber: getUnformattedClaimNumber(claimNumber),
      urlKey,
      sessionId,
      authKey: authToken,
      vin: vin,
    };
    // Show LoadingIndicator
    const eligibleServicesResponse = await getAutoEligibleServices(apiParamsData);
    if (eligibleServicesResponse && eligibleServicesResponse.response) {
      const flagData = store.getState().optimizelyFlags;
      const autoDrpSearchFlag: boolean = _get(flagData, 'data.autoDrpSearchSelection', true);

      const autoRentalShopSelection: boolean = _get(flagData, 'data.autoRentalShopSearchSelection', true);

      if (!autoDrpSearchFlag) {
        itemsToHide.push(EligibleServicesConstants.DRP);
      }

      if (!autoRentalShopSelection) {
        itemsToHide.push(EligibleServicesConstants.RENTAL);
      }

      /**
       * Feature flag logic that omits/removes all records stored in our itemsToHide array
       */
      for (const item of itemsToHide) {
        eligibleServicesResponse.response.eligibleServices = eligibleServicesResponse.response.eligibleServices.filter(
          (service: { serviceCode: string }) => service.serviceCode !== item
        );
      }

      if (isStaleSequence(sequence)) {
        logStaleResponseDropped({ action: 'auto eligible-services response', claimNumber, sequence, origin });
      } else {
        updateEligibleServicesState(eligibleServicesResponse, customerInfo, opCoInfo);
      }
    }
    return eligibleServicesResponse;
  } else if (claimType === GenericProductType.Home) {
    // Proceed with the getEligibility call
    let zipCodeFromClaimDetailsCall = _get(
      claimDetailResponse,
      'response[0].claimInfo.lossLocationAddress.postalCode',
      ''
    );
    // Trim the zipCodeFromClaimDetailsCall to make it 5 character long
    zipCodeFromClaimDetailsCall = zipCodeFromClaimDetailsCall.slice(0, 5);
    const zipCodeFromValidateCallResponse = _get(
      customerInfo,
      'claimsInfo.detailedPoliciesInfo[0].policyAddress[0].zipCode5',
      ''
    );
    const apiParamsData: PropertyEligibilityServiceCallArguments = {
      claimNumber: getUnformattedClaimNumber(claimNumber),
      urlKey,
      sessionId,
      authKey: authToken,
      // If the zip code is not present in validate call response, fallback to the one which is in claim details response
      zipCode: zipCodeFromValidateCallResponse ? zipCodeFromValidateCallResponse : zipCodeFromClaimDetailsCall,
    };
    // Show LoadingIndicator
    const eligibleServicesResponse = await getPropertyEligibleServices(apiParamsData);

    if (eligibleServicesResponse && eligibleServicesResponse.response) {
      // Update the eligibleServices API response based on the feature flag response
      const updatedEligibleServicesResponse = updateServiceEligibility(eligibleServicesResponse);
      if (isStaleSequence(sequence)) {
        logStaleResponseDropped({ action: 'property eligible-services response', claimNumber, sequence, origin });
      } else {
        updateEligibleServicesState(updatedEligibleServicesResponse, customerInfo, opCoInfo);
        updateToken(eligibleServicesResponse.response.updatedAuthToken);
      }
    }
    return eligibleServicesResponse;
  } else {
    return null;
  }
}

function updateServiceEligibility(
  eligibleServicesResponse: PropertyEligibilityServicesResponse
): PropertyEligibilityServicesResponse {
  const flagData = store.getState().optimizelyFlags;
  const propertyEmergencyServicesFlag: boolean = _get(flagData, 'data.propertyEmergencyServices', true);

  const propertyEmergencyWaterRemovalServicesFlag: boolean = _get(
    flagData,
    'data.propertyEmergencyWaterRemovalServices',
    true
  );

  const propertyPlnarServiceFlag: boolean = _get(flagData, 'data.propertyPlnarService', true);

  // Remove eligibleServices info from response if the flag is false
  if (!propertyEmergencyServicesFlag) {
    eligibleServicesResponse.response.eligibleServices = eligibleServicesResponse.response.eligibleServices.filter(
      (service) => !service.serviceTypeCode.includes(ServiceTypeCode.EMERGENCY_SERVICE_TYPE_CODE.toLowerCase())
    );
  }

  if (!propertyEmergencyWaterRemovalServicesFlag) {
    eligibleServicesResponse.response.eligibleServices = eligibleServicesResponse.response.eligibleServices.filter(
      (service) => !service.serviceTypeCode.includes(ServiceTypeCode.WATER_MITIGATION_TYPE_CODE.toLowerCase())
    );
  }

  // Update plnarEligible based on flag
  if (
    eligibleServicesResponse.response.plnarEligible === undefined ||
    (eligibleServicesResponse.response.plnarEligible === true && !propertyPlnarServiceFlag)
  ) {
    // If plnarEligible is undefined or propertyPlnarServiceFlag is false, set plnarEligible to false
    eligibleServicesResponse.response.plnarEligible = false;
  }
  return eligibleServicesResponse;
}

async function initiateClaimDetailsCall(
  claimNumber: string,
  urlKey: string,
  sessionId: string,
  autKey: string,
  sequence: number,
  origin: string
) {
  // Check if this is a Workers Comp claim to determine claimsSystem
  const state = store.getState();
  const allClaims = [
    ..._get(state, 'claims.openClaims', []),
    ..._get(state, 'claims.closedClaims', []),
    ..._get(state, 'claims.draftClaims', []),
  ];

  const claim = allClaims.find(
    (c: any) => getUnformattedClaimNumber(_get(c, 'claimNumber', '')) === getUnformattedClaimNumber(claimNumber)
  );

  // Determine claimsSystem for Workers Comp claims
  let claimsSystem: string | undefined;
  if (claim) {
    const claimType = getClaimType(_get(claim, 'policyType', ''));
    if (claimType === GenericProductType.WorkersComp) {
      // Workers Comp claims use EDDS system
      claimsSystem = _get(claim, 'claimsSystem', 'EDDS');
    }
  }

  // Call claim-detail API with claimsSystem parameter if applicable
  const claimDetailResponse = await getClaimDetails(claimNumber, urlKey, sessionId, autKey, claimsSystem);
  if (claimDetailResponse && !claimDetailResponse.statusCode) {
    if (isStaleSequence(sequence)) {
      logStaleResponseDropped({ action: 'claim-detail response', claimNumber, sequence, origin });
    } else {
      updateClaimDetailsState(claimDetailResponse, claimNumber);
    }
  }
  return claimDetailResponse;
}

function updateClaimDetailsState(claimDetailResponse: any, requestedClaimNumber: string) {
  store.dispatch(
    updateClaimDetailState({
      claimDetail: claimDetailResponse,
      requestedClaimNumber,
    })
  );
}

function updateEligibleServicesState(
  eligibleServicesResponse: PropertyEligibilityServicesResponse | AutoEligibilityServicesResponse,
  customerInfo: ContextRouterInfo,
  opcoInfo: string
) {
  store.dispatch(
    updateEligibleServiceState({
      response: {
        ...eligibleServicesResponse.response,
        opCo: opcoInfo,
      },
    })
  );
}

function updateToken(authToken: string) {
  store.dispatch(updateAuthToken(authToken));
}
